import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { LogOut, QrCode, Calendar, TrendingUp } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';
import NotificationBell from '@/components/NotificationBell';

const StudentDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [student, setStudent] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [classesThisWeek, setClassesThisWeek] = useState(0);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    fetchUserData();
    
    // Poll for payment updates every 30 seconds
    const pollInterval = setInterval(() => {
      if (student) {
        fetchPaymentStatus();
      }
    }, 30000);
    
    return () => clearInterval(pollInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [student?.student_id]);
  
  // Refresh QR code every 30 seconds
  useEffect(() => {
    if (student) {
      fetchQRCode();
      const interval = setInterval(fetchQRCode, 30000);
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [student]);
  
  const fetchUserData = async () => {
    try {
      const userRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
        credentials: 'include'
      });
      const userData = await userRes.json();
      setUser(userData);
      
      // Get student data
      const studentsRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/students`, {
        credentials: 'include'
      });
      const students = await studentsRes.json();
      const myStudent = students.find(s => s.user_id === userData.user_id);
      
      if (myStudent) {
        setStudent(myStudent);
        await fetchPaymentStatus(myStudent.student_id);
        await fetchWeeklyClasses();
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchQRCode = async () => {
    if (!student) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/students/${student.student_id}/qr`, {
        credentials: 'include'
      });
      const data = await response.json();
      setQrCode(data.totp_code);
    } catch (error) {
      console.error('Error fetching QR:', error);
    }
  };
  
  const fetchPaymentStatus = async (studentId = student?.student_id) => {
    if (!studentId) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/student/${studentId}`, {
        credentials: 'include'
      });
      const payments = await response.json();
      
      if (payments.length > 0) {
        const latestPayment = payments[0];
        const expiresAt = new Date(latestPayment.expires_at);
        const now = new Date();
        const daysUntilExpiry = Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24));
        
        let status = 'valid';
        if (expiresAt < now) status = 'expired';
        else if (daysUntilExpiry <= 3) status = 'expiring';
        
        setPaymentStatus({
          status,
          expiresAt,
          daysUntilExpiry: Math.max(0, daysUntilExpiry)
        });
      } else {
        setPaymentStatus({ status: 'expired', expiresAt: null, daysUntilExpiry: 0 });
      }
    } catch (error) {
      console.error('Error fetching payment:', error);
    }
  };
  
  const fetchWeeklyClasses = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/student/weekly-classes`,
        { credentials: 'include' }
      );
      
      if (response.ok) {
        const data = await response.json();
        setClassesThisWeek(data.classes_this_week);
      }
    } catch (error) {
      console.error('Error fetching weekly classes:', error);
    }
  };
  
  const handleLogout = async () => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-black">
        <div className="text-white text-xl font-heading">CARGANDO...</div>
      </div>
    );
  }
  
  if (!student) {
    return (
      <div className="min-h-screen bg-black p-4">
        <div className="max-w-md mx-auto mt-20 text-center">
          <h1 className="text-3xl font-heading text-white mb-4">PERFIL NO CONFIGURADO</h1>
          <p className="text-zinc-400 mb-6">Contacta al administrador para configurar tu perfil de estudiante.</p>
          <Button onClick={handleLogout} variant="outline">
            <LogOut className="mr-2 h-4 w-4" />
            Cerrar Sesión
          </Button>
        </div>
      </div>
    );
  }
  
  const getStatusColor = () => {
    if (!paymentStatus) return 'text-zinc-500';
    if (paymentStatus.status === 'valid') return 'text-green-500';
    if (paymentStatus.status === 'expiring') return 'text-yellow-500';
    return 'text-red-500';
  };
  
  const getStatusBg = () => {
    if (!paymentStatus) return 'bg-zinc-900/50 border-zinc-800';
    if (paymentStatus.status === 'valid') return 'bg-green-500/10 border-green-500/20';
    if (paymentStatus.status === 'expiring') return 'bg-yellow-500/10 border-yellow-500/20';
    return 'bg-red-500/10 border-red-500/20';
  };
  
  return (
    <div className="min-h-screen bg-black page-transition">
      {/* Header */}
      <div className="bg-zinc-950 border-b border-zinc-800 p-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {student.photo_url && (
              <img 
                src={student.photo_url} 
                alt={student.name}
                className="w-12 h-12 rounded-sm object-cover border-2 border-primary"
              />
            )}
            <div>
              <h2 className="text-lg font-heading text-white uppercase" data-testid="student-name">{student.name}</h2>
              <p className="text-sm text-zinc-400">{student.discipline}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <NotificationBell />
            <Button 
              onClick={handleLogout} 
              variant="ghost" 
              size="sm"
              data-testid="logout-button"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-4xl mx-auto p-4 space-y-6 pb-20">
        {/* QR Code Card */}
        <Card className="bg-zinc-950 border-zinc-800 p-6" data-testid="qr-code-card">
          <div className="text-center space-y-4">
            <div className="flex items-center justify-center space-x-2">
              <QrCode className="h-5 w-5 text-primary" />
              <h3 className="text-xl font-heading text-white uppercase">ID DIGITAL</h3>
            </div>
            
            {qrCode && (
              <div className="flex justify-center">
                <div className="qr-container">
                  <QRCodeSVG 
                    value={qrCode}
                    size={256}
                    level="H"
                    includeMargin={true}
                  />
                </div>
              </div>
            )}
            
            <p className="text-xs text-zinc-500 font-mono">Se actualiza cada 30 segundos</p>
          </div>
        </Card>
        
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Payment Status */}
          <Card className={`${getStatusBg()} border p-4`} data-testid="payment-status-card">
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Calendar className={`h-5 w-5 ${getStatusColor()}`} />
                <span className="text-xs uppercase tracking-wider text-zinc-400">Estado de Pago</span>
              </div>
              <div>
                <div className={`text-2xl font-bold font-heading ${getStatusColor()}`}>
                  {paymentStatus?.status === 'valid' && 'VIGENTE'}
                  {paymentStatus?.status === 'expiring' && 'POR VENCER'}
                  {paymentStatus?.status === 'expired' && 'VENCIDO'}
                </div>
                {paymentStatus?.expiresAt && (
                  <p className="text-sm text-zinc-400 mt-1">
                    {paymentStatus.status === 'expired' 
                      ? 'Renovar mensualidad'
                      : `${paymentStatus.daysUntilExpiry} días restantes`
                    }
                  </p>
                )}
              </div>
            </div>
          </Card>
          
          {/* Plan Type */}
          <Card className="bg-zinc-900/50 border-zinc-800/50 p-4" data-testid="plan-type-card">
            <div className="space-y-2">
              <span className="text-xs uppercase tracking-wider text-zinc-400">Membresía</span>
              <div>
                <div className="text-2xl font-bold font-heading text-white">
                  {student.plan_type}
                </div>
                <p className="text-sm text-zinc-400 mt-1">
                  {student.plan_type === 'Platinum' && '2 ingresos/día, todos los días'}
                  {student.plan_type === 'Gold' && '1 ingreso/día, todos los días'}
                  {student.plan_type === 'Silver' && '1 ingreso/día, máx 3 días/semana'}
                </p>
              </div>
            </div>
          </Card>
          
          {/* Weekly Classes */}
          {student.plan_type === 'Silver' && (
            <Card className="bg-zinc-900/50 border-zinc-800/50 p-4" data-testid="weekly-classes-card">
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <TrendingUp className="h-5 w-5 text-primary" />
                  <span className="text-xs uppercase tracking-wider text-zinc-400">Esta Semana</span>
                </div>
                <div>
                  <div className="text-2xl font-bold font-heading text-white">
                    {classesThisWeek}/3
                  </div>
                  <p className="text-sm text-zinc-400 mt-1">Días utilizados</p>
                </div>
              </div>
            </Card>
          )}
        </div>
        
        {/* Payment History */}
        <PaymentHistory />
        
        {/* Info Card */}
        <Card className="bg-zinc-950 border-zinc-800 p-6">
          <h3 className="text-lg font-heading text-white uppercase mb-4">INFORMACIÓN</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-zinc-500">Disciplina</span>
              <p className="text-white font-medium">{student.discipline}</p>
            </div>
            {student.grade && (
              <div>
                <span className="text-zinc-500">Grado/Khan</span>
                <p className="text-white font-medium">{student.grade}</p>
              </div>
            )}
            {student.blood_type && (
              <div>
                <span className="text-zinc-500">Tipo de Sangre</span>
                <p className="text-white font-medium">{student.blood_type}</p>
              </div>
            )}
            {student.emergency_contact && (
              <div>
                <span className="text-zinc-500">Contacto de Emergencia</span>
                <p className="text-white font-medium">{student.emergency_contact}</p>
                {student.emergency_phone && (
                  <p className="text-zinc-400 text-xs">{student.emergency_phone}</p>
                )}
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

// Payment History Component
const PaymentHistory = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  
  useEffect(() => {
    fetchPaymentHistory();
  }, []);
  
  const fetchPaymentHistory = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/student/payment-history`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setPayments(data.payments);
      }
    } catch (error) {
      console.error('Error fetching payment history:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-500/20 text-green-500',
      expiring: 'bg-yellow-500/20 text-yellow-500',
      expired: 'bg-red-500/20 text-red-500'
    };
    
    const labels = {
      active: 'Vigente',
      expiring: 'Por vencer',
      expired: 'Vencido'
    };
    
    return (
      <span className={`px-2 py-1 rounded-sm text-xs font-bold uppercase ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };
  
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-BO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  const displayedPayments = expanded ? payments : payments.slice(0, 3);
  
  return (
    <Card className="bg-zinc-950 border-zinc-800 p-6" data-testid="payment-history-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-heading text-white uppercase">HISTORIAL DE PAGOS</h3>
        <span className="text-sm text-zinc-500">{payments.length} registros</span>
      </div>
      
      {loading ? (
        <div className="py-4 text-center text-zinc-500">Cargando...</div>
      ) : payments.length === 0 ? (
        <div className="py-4 text-center text-zinc-500">No hay pagos registrados</div>
      ) : (
        <>
          <div className="space-y-3">
            {displayedPayments.map((payment) => (
              <div 
                key={payment.payment_id}
                className="flex items-center justify-between p-3 bg-zinc-900 rounded-sm border border-zinc-800"
                data-testid={`payment-${payment.payment_id}`}
              >
                <div>
                  <p className="text-white font-bold">Bs {payment.amount.toLocaleString()}</p>
                  <p className="text-xs text-zinc-500">
                    {formatDate(payment.payment_date)} → {formatDate(payment.expires_at)}
                  </p>
                </div>
                {getStatusBadge(payment.status)}
              </div>
            ))}
          </div>
          
          {payments.length > 3 && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="w-full mt-3 py-2 text-sm text-primary hover:text-primary/80 transition-colors"
            >
              {expanded ? 'Ver menos' : `Ver ${payments.length - 3} más`}
            </button>
          )}
        </>
      )}
    </Card>
  );
};

export default StudentDashboard;
